import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import PoemPairing from "@/components/PoemPairing";
import ContestNavigation from "@/components/ContestNavigation";
import RoundSummary from "@/components/RoundSummary";
import { getPoemTitle, getPoemContentById, getPoemContent, isPoemArchived } from "@/utils/poemUtils";
import { useContestState } from "@/hooks/useContestState";
import { 
  createDeathmatch, 
  addRoundToDeathmatch, 
  completeDeathmatch,
  getDeathmatchDetails,
  recordBracketPositions,
  determineRoundType,
} from "@/services/deathmatchService";
import { getPoemById } from "@/services/poemService";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Database } from "@/types/supabase";
import { Button } from "@/components/ui/button";

type Poem = Database["public"]["Tables"]["poems"]["Row"];

interface PairingState {
  bracketPositions: Array<{ 
    poet: string; 
    poemIndex: number; 
    poemId: string;
    poet_id?: string; // Add optional poet_id field
    subject?: string; // Add optional subject field to the state passed via navigation
  }>;
  pairings: Array<[number, number]>;
}

// Define type for the poem details map
interface PoemDetails {
  title: string;
  poet: string;
  content?: string;
}

// Storage keys (duplicated from useContestState for reset function)
const STORAGE_KEYS = {
  CURRENT_PAIRING_INDEX: 'deathmatch_current_pairing_index',
  WINNERS: 'deathmatch_winners',
  BRACKET_POSITIONS: 'deathmatch_bracket_positions',
  PAIRINGS: 'deathmatch_pairings',
  VIEW_SUMMARY: 'deathmatch_view_summary',
  TIMESTAMP: 'deathmatch_timestamp',
  ROUND_COMPLETED: 'deathmatch_round_completed',
  DEATHMATCH_ID: 'deathmatch_id',
};

// Define the structure of the state passed via navigation
interface ContestLocationState {
  bracketPositions: Array<{ poet: string; poemIndex: number; poemId?: string; poet_id?: string }>;
  pairings: Array<[number, number]>;
  subject?: string; // Subject is part of the location state
}

// ADD: Define type for the setup data
interface ContestSetupData {
  bracketPositions: Array<{ poet: string; poemIndex: number; poemId: string; poet_id?: string }>;
  pairings: Array<[number, number]>;
  subject: string;
}

const DeathMatchContest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // ADDED: State for loading setup data
  const [isLoadingSetup, setIsLoadingSetup] = useState(true);
  const [contestSetup, setContestSetup] = useState<ContestSetupData | null>(null);
  
  // REVISED: useEffect to load setup data from location.state or localStorage
  useEffect(() => {
    let loadedData: ContestSetupData | null = null;
    // No need to track source anymore
    const locationState = location.state as ContestLocationState | null;

    // 1. Try location.state first
    if (locationState?.bracketPositions && locationState?.pairings && locationState?.subject) {
      console.log("Loading contest setup from location.state");
      const validatedPositions = locationState.bracketPositions
        .filter(pos => typeof pos.poemId === 'string')
        .map(pos => ({ ...pos, poemId: pos.poemId as string }));

      if (validatedPositions.length === locationState.bracketPositions.length) {
          loadedData = {
              bracketPositions: validatedPositions,
              pairings: locationState.pairings,
              subject: locationState.subject
          };
      } else {
           console.warn("Invalid bracket data found in location.state. Some poemIds missing.");
      }
    }

    // 2. Try localStorage as fallback only if state didn't load
    if (!loadedData) {
        console.log("location.state missing or invalid, trying localStorage...");
        try {
          const storedSetupJson = localStorage.getItem('deathmatch_setup');
          if (storedSetupJson) {
            const storedSetup = JSON.parse(storedSetupJson);
            if (storedSetup.bracketPositions && storedSetup.pairings && storedSetup.subject && Array.isArray(storedSetup.bracketPositions)) {
               const validatedStoredPositions = storedSetup.bracketPositions
                 .filter((pos: any) => typeof pos.poemId === 'string')
                 .map((pos: any) => ({ ...pos, poemId: pos.poemId as string }));

               if (validatedStoredPositions.length === storedSetup.bracketPositions.length) {
                  console.log("Loading contest setup from localStorage");
                  loadedData = {
                     bracketPositions: validatedStoredPositions,
                     pairings: storedSetup.pairings,
                     subject: storedSetup.subject
                  };
               } else {
                    console.warn("Invalid data found in localStorage 'deathmatch_setup' (missing poemIds).");
               }
            } else {
               console.warn("Invalid structure found in localStorage 'deathmatch_setup'.");
            }
          } else {
             console.log("No setup data found in localStorage.");
          }
        } catch (error) {
          console.error("Error reading/parsing setup from localStorage:", error);
        }
    }

    // Final decision based on loadedData
    if (loadedData) {
      setContestSetup(loadedData);
    } else {
      // Only redirect if BOTH state and localStorage failed
      console.error("No valid contest setup data found from location.state OR localStorage. Redirecting to /deathmatch.");
      toast({ title: "Error", description: "Could not load contest data. Returning to setup.", variant: "destructive" });
      navigate('/deathmatch', { replace: true });
    }
    setIsLoadingSetup(false);
  }, [location.state, navigate, toast]); 
  
  // --- Loading Check --- 
  if (isLoadingSetup) { // Check only isLoadingSetup initially
      return (
          <div className="flex justify-center items-center h-[80vh]">
              <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                  <h2 className="text-xl font-poetry">Loading Contest...</h2>
              </div>
          </div>
      );
  }
  // --- End Loading Check ---

  // --- Render Content ONLY if setup is loaded --- 
  // If loading is finished BUT contestSetup is still null, something went wrong (redirect handled in useEffect)
  if (!contestSetup) {
      // This should ideally not be reached if the loading useEffect works correctly, but acts as a safeguard.
      console.error("Rendering attempted without contestSetup. This indicates an issue.");
      return (
         <div className="flex justify-center items-center h-[80vh]">
             <div className="text-center">
                 <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-4" />
                 <h2 className="text-xl font-poetry text-destructive">Error Loading Contest Data</h2>
                 <p className="text-muted-foreground">Please return to the Deathmatch setup page.</p>
                 <Button onClick={() => navigate('/deathmatch', { replace: true })} className="mt-4">Go to Setup</Button>
             </div>
         </div>
      );
  }
  
  // If we reach here, contestSetup is loaded and valid
  return <RenderContestContent contestSetup={contestSetup} />;
};

// NEW: Extracted component to render only when setup data is ready
interface RenderContestContentProps {
    contestSetup: ContestSetupData;
}

const RenderContestContent: React.FC<RenderContestContentProps> = ({ contestSetup }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Now, derive initial values *inside* the component that receives valid contestSetup
  const initialBracketPositions = contestSetup.bracketPositions;
  const initialPairings = contestSetup.pairings;
  const initialSubjectFromState = contestSetup.subject;

  // Prepare state for the hook
  const hookInitialState = {
    bracketPositions: initialBracketPositions,
    pairings: initialPairings,
  };

  // Call the hook ONLY when initial state is valid
  const { 
    currentPairingIndex,
    winner,
    winners,
    isContestCompleted,
    championPosition,
    isViewingSummary,
    handlePoemWin,
    navigateToPairing,
    toggleSummaryView,
    totalPairings,
    setCurrentPairingIndex,
  } = useContestState(hookInitialState); 
  
  const [contestSubject, setContestSubject] = useState<string>(initialSubjectFromState);
  useEffect(() => {
    setContestSubject(initialSubjectFromState);
  }, [initialSubjectFromState]);
  
  // State for data fetching (remains the same)
  const [poemsData, setPoemsData] = useState<Record<string, PoemDetails>>({});
  
  // Reset localStorage if needed (moved inside RenderContestContent)
  const resetLocalStorageIfNeeded = () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.CURRENT_PAIRING_INDEX);
      localStorage.removeItem(STORAGE_KEYS.WINNERS);
      // Keep BRACKET_POSITIONS and PAIRINGS (managed by setup page)
      localStorage.removeItem(STORAGE_KEYS.VIEW_SUMMARY);
      localStorage.removeItem(STORAGE_KEYS.TIMESTAMP);
      localStorage.removeItem(STORAGE_KEYS.ROUND_COMPLETED);
      // Keep DEATHMATCH_ID
    } catch (error) {
      console.error("Error resetting localStorage progress:", error);
    }
  };
  
  // Track if this is the first render
  const isFirstRender = useRef(true);
  // Use a ref to track if we're currently saving to prevent multiple executions
  const isSavingRef = useRef(false);
  // Use a ref to track if we're currently checking round completion
  const isCheckingRoundRef = useRef(false);
  
  const [deathmatchId, setDeathmatchId] = useState<string | null>(null);
  const [isCreatingDeathmatch, setIsCreatingDeathmatch] = useState(false);
  const [isSavingRound, setIsSavingRound] = useState(false);
  const [poemContent1, setPoemContent1] = useState<string>("");
  const [poemContent2, setPoemContent2] = useState<string>("");
  const [poemTitle1, setPoemTitle1] = useState<string>("Loading...");
  const [poemTitle2, setPoemTitle2] = useState<string>("Loading...");
  const [isLoadingPoemContent, setIsLoadingPoemContent] = useState(false);
  const [isPoemArchived1, setIsPoemArchived1] = useState(false);
  const [isPoemArchived2, setIsPoemArchived2] = useState(false);
  const [voteRecorded, setVoteRecorded] = useState(false);
  const [dbError, setDbError] = useState(false);
  const [deathmatchCompleted, setDeathmatchCompleted] = useState(false);
  const [isRoundCompleted, setIsRoundCompleted] = useState(false);
  const [showRoundSummaryModal, setShowRoundSummaryModal] = useState(false);
  const [currentRoundType, setCurrentRoundType] = useState<string>("Quarterfinals");
  const [nextRoundType, setNextRoundType] = useState<string>("Semifinals");
  const [poemDetailsMap, setPoemDetailsMap] = useState<Map<string, PoemDetails>>(new Map());
  
  // Determine current round type based on pairing index
  useEffect(() => {
    if (initialPairings.length > 0) {
      const round = determineRoundType(currentPairingIndex + 1, initialPairings.length);
      let nextRoundIndex = currentPairingIndex + 1;
      while (nextRoundIndex < initialPairings.length && winners[nextRoundIndex] !== null) {
        nextRoundIndex++;
      }
      const next = nextRoundIndex < initialPairings.length 
        ? determineRoundType(nextRoundIndex + 1, initialPairings.length) 
        : "Final Results";
      setCurrentRoundType(round);
      setNextRoundType(next);
    }
  }, [currentPairingIndex, initialPairings, winners]);
  
  // Clear round completion state on component mount
  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.ROUND_COMPLETED);
      setIsRoundCompleted(false);
      setShowRoundSummaryModal(false);
      isSavingRef.current = false;
      isCheckingRoundRef.current = false;
    } catch (error) {
      console.error("Error clearing round completion state:", error);
    }
  }, []);

  // Check localStorage for round completion on mount/refresh
  useEffect(() => {
    if (!showRoundSummaryModal && !isCheckingRoundRef.current) {
      try {
        const storedRoundCompleted = localStorage.getItem(STORAGE_KEYS.ROUND_COMPLETED) === 'true';
        if (storedRoundCompleted && !isRoundCompleted) {
          setIsRoundCompleted(true);
          setShowRoundSummaryModal(true);
        }
      } catch (error) {
        console.error("Error checking round completion:", error);
      }
    }
  }, [showRoundSummaryModal, isRoundCompleted]);

  // Combined Initialization: Create Deathmatch & Fetch Poem Details
  useEffect(() => {
    let isMounted = true; 
    const initializeContest = async () => {
      // contestSetup is guaranteed to be valid here
      
      // Log before reading
      const idToCheck = localStorage.getItem(STORAGE_KEYS.DEATHMATCH_ID);
      console.log(`[DeathMatchContest.tsx] initializeContest: Checking localStorage for DEATHMATCH_ID: ${idToCheck}`);
      let currentDeathmatchId = idToCheck;
      
      setDeathmatchId(currentDeathmatchId);

      if (!currentDeathmatchId) {
        if (isMounted) setIsCreatingDeathmatch(true);
        try {
          console.log("Creating new Deathmatch...");
          const poemIds = contestSetup.bracketPositions.map(pos => pos.poemId);
          const deathmatch = await createDeathmatch(poemIds);
          if (isMounted) {
            currentDeathmatchId = deathmatch.id;
            setDeathmatchId(currentDeathmatchId);
            localStorage.setItem(STORAGE_KEYS.DEATHMATCH_ID, currentDeathmatchId); 
            setDbError(false);
            console.log("Created Deathmatch ID:", currentDeathmatchId);
          }
        } catch (error) {
          console.error("Error creating Deathmatch record:", error);
          if (isMounted) {
            toast({ title: "Error", description: "Failed to create deathmatch record.", variant: "destructive" });
            setDbError(true);
          }
        } finally {
          if (isMounted) setIsCreatingDeathmatch(false);
        }
      } else {
         console.log("Using existing Deathmatch ID:", currentDeathmatchId);
      }

      if (poemDetailsMap.size === 0 && currentDeathmatchId) { 
        console.log("Fetching details for all poems...");
        try {
            const poemIds = contestSetup.bracketPositions.map(pos => pos.poemId);
            const uniquePoemIds = [...new Set(poemIds)];
            const detailsMap = new Map<string, PoemDetails>();
            const fetchPromises = uniquePoemIds.map(async (poemId) => {
              try {
                  const poemData = await getPoemById(poemId);
                  if (!poemData) throw new Error(`Poem data null for ID: ${poemId}`);
                  detailsMap.set(poemId, { 
                      title: poemData.title || "Untitled", 
                      poet: poemData.poet_name || "Unknown", 
                      content: poemData.content || ""
                  });
              } catch (fetchError) {
                  console.error(`Error fetching details for poemId ${poemId}:`, fetchError);
                  const originalPoet = contestSetup.bracketPositions.find(p => p.poemId === poemId)?.poet || 'Unknown';
                  detailsMap.set(poemId, { title: 'Error Fetching', poet: originalPoet });
              }
            });
            await Promise.all(fetchPromises);
            if (isMounted) {
              setPoemDetailsMap(detailsMap);
              console.log("Fetched poem details map.");
            }
          } catch (error) {
            console.error("Error fetching poem details:", error);
            if (isMounted) toast({ title: "Error", description: "Failed to load poem details.", variant: "destructive" });
          }
        }
      };
    initializeContest();
    return () => { isMounted = false; };
  }, [contestSetup, toast]); // Depend on contestSetup object

  // Load poem contents when current pairing changes
  useEffect(() => {
    // Determine dynamic positions FIRST
    let pos1: number | null = null;
    let pos2: number | null = null;
    if (currentPairingIndex >= 0 && currentPairingIndex <= 3) {
      const currentPairing = contestSetup.pairings[currentPairingIndex];
      if (currentPairing) [pos1, pos2] = currentPairing;
    } else if (currentPairingIndex === 4) {
      pos1 = winners[0]; pos2 = winners[1];
    } else if (currentPairingIndex === 5) {
      pos1 = winners[2]; pos2 = winners[3];
    } else if (currentPairingIndex === 6) {
      pos1 = winners[4]; pos2 = winners[5];
    }
    
    // Check if positions are valid before proceeding
    if (pos1 === null || pos2 === null) {
        console.warn(`loadPoemData skipped for index ${currentPairingIndex} due to null positions.`);
        // Optionally set loading state false or show an error/placeholder
        setIsLoadingPoemContent(false);
        setPoemTitle1("Error"); setPoemContent1("[");
        setPoemTitle2("Error"); setPoemContent2("]");
        return; // Stop if positions couldn't be determined
    }
    
    // Use final determined pos1 and pos2
    const position1 = pos1;
    const position2 = pos2;
    
    // Original currentPairing logic removed as positions are now determined dynamically above
    // const currentPairing = contestSetup.pairings[currentPairingIndex];
    // if (!currentPairing) return;
    
    const loadPoemData = async () => {
      setIsLoadingPoemContent(true);
      setPoemTitle1("Loading..."); setPoemTitle2("Loading...");
      setPoemContent1(""); setPoemContent2("");
      try {
        // const [position1, position2] = currentPairing; // Removed
        const poemId1 = contestSetup.bracketPositions[position1]?.poemId;
        const poemId2 = contestSetup.bracketPositions[position2]?.poemId;

        const fetchPoemDetails = async (poemId: string | undefined, fallbackPoetName: string): Promise<{ title: string; content: string; isArchived: boolean }> => {
          if (!poemId) return { title: "Unknown", content: `[No data for ${fallbackPoetName}]`, isArchived: false };
          try {
            const poemData = await getPoemById(poemId);
            if (!poemData) throw new Error(`Poem data null for ID: ${poemId}`);
            return { 
                title: poemData.title || "Untitled", 
                content: poemData.content || "", 
                isArchived: (poemData as any).is_archived === true 
            };
          } catch (error) {
            console.error(`Error fetching poem ${poemId}:`, error);
            return { title: `Error: ${fallbackPoetName}`, content: `[Content unavailable]`, isArchived: false };
          }
        };

        const fallbackPoet1 = contestSetup.bracketPositions[position1]?.poet || 'Unknown';
        const fallbackPoet2 = contestSetup.bracketPositions[position2]?.poet || 'Unknown';
        const [details1, details2] = await Promise.all([ fetchPoemDetails(poemId1, fallbackPoet1), fetchPoemDetails(poemId2, fallbackPoet2) ]);
        
        setPoemTitle1(details1.title); setPoemContent1(details1.content); setIsPoemArchived1(details1.isArchived);
        setPoemTitle2(details2.title); setPoemContent2(details2.content); setIsPoemArchived2(details2.isArchived);

        if (details1.isArchived || details2.isArchived) toast({ title: "Archived Poem", description: "An archived poem is in this match.", duration: 6000 });
      } catch (error) {
        console.error("Error loading poem data:", error);
        setPoemTitle1("Error"); setPoemContent1("[Error]");
        setPoemTitle2("Error"); setPoemContent2("[Error]");
      } finally { setIsLoadingPoemContent(false); }
    };
    loadPoemData();
    // Keep dependencies: re-run when index changes or winners potentially update for later rounds
  }, [currentPairingIndex, contestSetup, toast, winners]); // Added winners dependency

  // Handle winner selection (only mark vote recorded)
  useEffect(() => {
    if (winner !== null && !deathmatchCompleted && !voteRecorded) {
      setVoteRecorded(true);
    }
  }, [winner, deathmatchCompleted, voteRecorded]);
  
  // Check if the current round is complete (logic remains the same)
  const checkRoundCompletion = () => {
      let isRoundComplete = false;
      if (totalPairings === 7 && currentPairingIndex === 3 && winners.slice(0, 4).every(w => w !== null)) {
          console.log("Quarterfinals complete");
          isRoundComplete = true;
      } else if (totalPairings === 7 && currentPairingIndex === 5 && winners[4] !== null && winners[5] !== null) {
          console.log("Semifinals complete");
          isRoundComplete = true;
      } else if (currentPairingIndex === totalPairings - 1 && winners[currentPairingIndex] !== null) {
          console.log("Final round completed");
          saveCurrentRoundToDatabase().then(() => toggleSummaryView(true));
          return; 
      }
      if (isRoundComplete) {
          setIsRoundCompleted(true);
          setShowRoundSummaryModal(true);
          try { localStorage.setItem(STORAGE_KEYS.ROUND_COMPLETED, 'true'); } catch (error) {
              console.error("Error saving round completion status:", error);
          }
      }
  };
  
  // Save round results to DB (logic remains the same, uses contestSetup)
  const saveCurrentRoundToDatabase = async () => {
      if (!deathmatchId || dbError || isSavingRef.current) {
            console.log("Skipping DB save:", { deathmatchId, dbError, isSaving: isSavingRef.current });
            return;
        }
        isSavingRef.current = true;
        console.log(`Saving results to DB for round ending at index: ${currentPairingIndex}`);
        try {
            let justCompletedPairIndices: number[] = [];
            const roundType = determineRoundType(currentPairingIndex + 1, totalPairings);
            if (totalPairings === 7) {
                if (roundType === "Quarterfinals") justCompletedPairIndices = [0, 1, 2, 3];
                else if (roundType === "Semifinals") justCompletedPairIndices = [4, 5];
                else if (roundType === "Final") justCompletedPairIndices = [6];
            } else {
                justCompletedPairIndices = [currentPairingIndex];
            }
            const savePromises = justCompletedPairIndices.map(async (pairIndex) => {
                const pair = initialPairings[pairIndex];
                const winnerPosition = winners[pairIndex];
                if (pair && winnerPosition !== null) {
                    const [pos1, pos2] = pair;
                    const poemId1 = initialBracketPositions[pos1]?.poemId;
                    const poemId2 = initialBracketPositions[pos2]?.poemId;
                    const winnerPoemId = initialBracketPositions[winnerPosition]?.poemId;
                    if (typeof poemId1 === 'string' && typeof poemId2 === 'string' && typeof winnerPoemId === 'string') {
                        await addRoundToDeathmatch({
                            deathmatch_id: deathmatchId,
                            round_number: pairIndex + 1,
                            poem1_id: poemId1,
                            poem2_id: poemId2,
                            winner_id: winnerPoemId,
                        });
                    } else {
                        console.error(`Skipping save for pairing ${pairIndex + 1}: Invalid poemId(s).`);
                    }
                }
            });
            await Promise.all(savePromises);
            console.log("Finished saving round results to DB.");
        } catch (error) {
            console.error("Error saving round results:", error);
            toast({ title: "Save Error", description: "Failed to save results.", variant: "destructive" });
            setDbError(true);
        } finally {
            isSavingRef.current = false;
        }
  };

  // ... (rest of handlers: handleViewSummary, handleCloseModal, handleAdvanceToNextRound)
    const handleViewSummary = async () => {
        const isFinalRound = currentPairingIndex === totalPairings - 1;
        if (isFinalRound && winners[currentPairingIndex] !== null) {
            await saveCurrentRoundToDatabase();
            toast({ title: "Deathmatch Completed!", description: "Champion crowned!", duration: 3000 });
        }
        toggleSummaryView(true);
    };

    const handleCloseModal = () => setShowRoundSummaryModal(false);

    const handleAdvanceToNextRound = () => {
        if (currentRoundType.toLowerCase() === 'final') {
            setShowRoundSummaryModal(false);
            setIsRoundCompleted(false);
            toggleSummaryView(true);
            localStorage.removeItem(STORAGE_KEYS.ROUND_COMPLETED);
            return;
        }
        let nextRoundStartIndex = (currentPairingIndex <= 3) ? 4 : (currentPairingIndex <= 5 ? 6 : totalPairings);
        setShowRoundSummaryModal(false);
        setIsRoundCompleted(false);
        try { localStorage.removeItem(STORAGE_KEYS.ROUND_COMPLETED); } catch (error) { console.error("Error clearing round status:", error); }
        // Use state update function for reliable navigation
        setCurrentPairingIndex(nextRoundStartIndex);
    };
  
  // --- Render logic --- 
  // Derive necessary variables from state/props
  
  // *** START Fix: Dynamically determine participants for later rounds ***
  let position1: number | null = null;
  let position2: number | null = null;

  if (currentPairingIndex >= 0 && currentPairingIndex <= 3) { // Quarterfinals
    const currentPairing = contestSetup.pairings[currentPairingIndex];
    if (currentPairing) {
      [position1, position2] = currentPairing;
    }
  } else if (currentPairingIndex === 4) { // Semifinal 1
    position1 = winners[0]; // Winner of pairing index 0
    position2 = winners[1]; // Winner of pairing index 1
  } else if (currentPairingIndex === 5) { // Semifinal 2
    position1 = winners[2]; // Winner of pairing index 2
    position2 = winners[3]; // Winner of pairing index 3
  } else if (currentPairingIndex === 6) { // Final
    position1 = winners[4]; // Winner of pairing index 4
    position2 = winners[5]; // Winner of pairing index 5
  }
  
  // Handle cases where a position might still be null (e.g., previous round winner not decided yet)
  // This might happen if navigation logic allows reaching round 4 before winners[0]/[1] are set.
  // For safety, add a check before rendering.
  if (position1 === null || position2 === null) {
    console.error(`Invalid pairing state for index ${currentPairingIndex}. Determined positions:`, { position1, position2 }, "Winners:", winners);
    // Render an error or loading state, or redirect
    return <div>Error: Could not determine participants for this round. Please check console.</div>;
  }
  // *** END Fix ***

  const validBracketPositionsForSummary = contestSetup.bracketPositions.filter(
      (pos): pos is { poet: string; poemIndex: number; poemId: string; poet_id?: string } => 
        typeof pos.poemId === 'string' && pos.poemId !== ''
  );
  const isRoundLocked = isRoundCompleted; // Round locked when completion modal is shown
  
  // Custom next handler
  const handleNext = async () => {
    const isLastOfInitialRound = currentPairingIndex === 3 && winners.slice(0, 4).every(w => w !== null);
    const isLastOfSemifinalsRound = currentPairingIndex === 5 && winners[4] !== null && winners[5] !== null;
    const isLastOfFinalRound = currentPairingIndex === totalPairings - 1 && winners[currentPairingIndex] !== null;
    
    if ((isLastOfInitialRound || isLastOfSemifinalsRound || isLastOfFinalRound) && winner !== null) {
      await saveCurrentRoundToDatabase();
      checkRoundCompletion();
    } else {
      navigateToPairing('next');
    }
  };
  
  // Conditional rendering for DB error or loading state
  if (isCreatingDeathmatch) {
    return <div className="flex justify-center items-center h-[80vh]"><Loader2 className="h-8 w-8 animate-spin mr-2" /> Setting up...</div>;
  }
  if (dbError) {
    return (
        <div className="max-w-6xl mx-auto py-8 px-4">
            <Alert variant="destructive" className="mb-6"><AlertCircle className="h-4 w-4" /><AlertTitle>Database Error</AlertTitle><AlertDescription>Cannot connect to database. Results will not be saved.</AlertDescription></Alert>
            {/* Render main content even with DB error */} 
            {renderMainContent()}
        </div>
    );
  }
  
  // Main render function (extracted)
  function renderMainContent() {
    return (
      <div className="max-w-6xl mx-auto pt-16 pb-8 px-4 animate-in fade-in duration-300 mt-6">
        <style>{`.custom-scrollbar { ... }` /* Styles */}</style>
        <ContestNavigation 
          currentPairingIndex={currentPairingIndex}
          totalPairings={totalPairings}
          onPrevious={() => navigateToPairing('prev')}
          onNext={handleNext}
          onReturn={() => navigate('/deathmatch', { state: { subject: contestSubject } })}
          onViewSummary={handleViewSummary}
          disableNext={winner === null}
          isContestCompleted={isContestCompleted}
          isViewingSummary={isViewingSummary}
          currentRound={currentRoundType}
          isRoundCompleted={isRoundCompleted}
        />
        
        {isViewingSummary ? (
          <RoundSummary
            bracketPositions={validBracketPositionsForSummary}
            pairings={contestSetup.pairings}
            winners={winners}
            championPosition={championPosition}
            poemDetailsMap={poemDetailsMap}
            onClose={() => toggleSummaryView(false)}
            onReturnToBracket={() => navigate('/deathmatch', { state: { subject: contestSubject } })}
            totalPairings={totalPairings}
            currentPairingIndex={currentPairingIndex}
            isFinalRound={winners[totalPairings - 1] !== null}
          />
        ) : (
          <div className={`${isLoadingPoemContent ? 'opacity-50' : 'opacity-100'} transition-opacity duration-300 relative`}>
            <PoemPairing
              poet1={contestSetup.bracketPositions[position1!]?.poet}
              poet2={contestSetup.bracketPositions[position2!]?.poet}
              poemTitle1={poemTitle1}
              poemTitle2={poemTitle2}
              poemContent1={poemContent1}
              poemContent2={poemContent2}
              handlePoemWin={handlePoemWin}
              position1={position1!}
              position2={position2!}
              winner={winner}
              isRoundLocked={isRoundLocked}
              isPoemArchived1={isPoemArchived1}
              isPoemArchived2={isPoemArchived2}
            />
            {isLoadingPoemContent && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-white/50">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            )}
          </div>
        )}
        
        <Dialog open={showRoundSummaryModal} onOpenChange={(open) => !open && handleCloseModal()}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto custom-scrollbar" onEscapeKeyDown={handleCloseModal} onInteractOutside={handleCloseModal}>
            <DialogTitle className="sr-only">Round Results</DialogTitle>
            <DialogDescription className="sr-only">Summary of completed round.</DialogDescription>
            <RoundSummary 
              bracketPositions={validBracketPositionsForSummary}
              pairings={contestSetup.pairings}
              winners={winners}
              championPosition={championPosition}
              onClose={handleCloseModal}
              onReturnToBracket={() => navigate('/deathmatch', { state: { subject: contestSubject } })}
              totalPairings={totalPairings}
              currentPairingIndex={currentPairingIndex}
              poemDetailsMap={poemDetailsMap}
              limitToPairingIndex={currentPairingIndex}
              onAdvanceToNextRound={handleAdvanceToNextRound}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return renderMainContent();
};

// Export the main component
export default DeathMatchContest;
